import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { ReserveHeader } from "@/components/ReserveHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { uploadPortfolioFile, deletePortfolioFile, portfolioSignedUrl } from "@/lib/portfolio";

export const Route = createFileRoute("/_authenticated/portfolio")({
  head: () => ({ meta: [{ title: "My Portfolio — The Hospitality Reserve" }] }),
  component: PortfolioEditPage,
});

type Profile = {
  id: string;
  full_name: string | null;
  headline: string | null;
  years_experience: number | null;
  specialties: string[] | null;
  avatar_path: string | null;
  about_me: string | null;
};

type Media = { id: string; kind: "photo" | "flair_video"; storage_path: string; caption: string | null; sort_order: number };
type Cert = { id: string; name: string; issuer: string | null; issued_on: string | null; credential_url: string | null };
type PVenue = { id: string; venue_name: string; role: string | null; city: string | null; started_on: string | null; ended_on: string | null; description: string | null; photo_path: string | null };

function PortfolioEditPage() {
  const [userId, setUserId] = useState<string | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [media, setMedia] = useState<Media[]>([]);
  const [mediaUrls, setMediaUrls] = useState<Record<string, string>>({});
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [certs, setCerts] = useState<Cert[]>([]);
  const [pvenues, setPVenues] = useState<PVenue[]>([]);
  const [venueUrls, setVenueUrls] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [savingProfile, setSavingProfile] = useState(false);
  const [specialtyDraft, setSpecialtyDraft] = useState("");

  const load = async () => {
    setLoading(true);
    const { data: { session: __s } } = await supabase.auth.getSession(); const u = { user: __s?.user ?? null };
    if (!u.user) return;
    setUserId(u.user.id);
    const [{ data: p }, { data: m }, { data: c }, { data: v }] = await Promise.all([
      supabase.from("profiles").select("id,full_name,headline,years_experience,specialties,avatar_path,about_me").eq("id", u.user.id).maybeSingle(),
      (supabase.from as any)("portfolio_media").select("*").eq("bartender_id", u.user.id).order("sort_order"),
      (supabase.from as any)("portfolio_certifications").select("*").eq("bartender_id", u.user.id).order("sort_order"),
      (supabase.from as any)("portfolio_venues").select("*").eq("bartender_id", u.user.id).order("sort_order"),
    ]);
    setProfile(p as Profile);
    setMedia((m ?? []) as Media[]);
    setCerts((c ?? []) as Cert[]);
    setPVenues((v ?? []) as PVenue[]);
    setAvatarUrl(await portfolioSignedUrl((p as Profile | null)?.avatar_path));
    const mu: Record<string, string> = {};
    await Promise.all((m ?? []).map(async (row: Media) => { const url = await portfolioSignedUrl(row.storage_path); if (url) mu[row.id] = url; }));
    setMediaUrls(mu);
    const vu: Record<string, string> = {};
    await Promise.all((v ?? []).map(async (row: PVenue) => { const url = await portfolioSignedUrl(row.photo_path); if (url) vu[row.id] = url; }));
    setVenueUrls(vu);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const saveProfile = async () => {
    if (!profile || !userId) return;
    setSavingProfile(true);
    const { error } = await supabase.from("profiles").update({
      headline: profile.headline,
      years_experience: profile.years_experience,
      specialties: profile.specialties,
      about_me: profile.about_me,
    }).eq("id", userId);
    setSavingProfile(false);
    if (error) toast.error(error.message); else toast.success("Profile saved.");
  };

  const onAvatarUpload = async (file: File) => {
    if (!userId) return;
    try {
      if (profile?.avatar_path) await deletePortfolioFile(profile.avatar_path);
      const path = await uploadPortfolioFile(userId, file, "avatar");
      const { error } = await supabase.from("profiles").update({ avatar_path: path }).eq("id", userId);
      if (error) throw error;
      toast.success("Avatar updated.");
      await load();
    } catch (e: any) { toast.error(e.message ?? "Upload failed."); }
  };

  const addSpecialty = () => {
    const t = specialtyDraft.trim();
    if (!t || !profile) return;
    setProfile({ ...profile, specialties: [...(profile.specialties ?? []), t] });
    setSpecialtyDraft("");
  };
  const removeSpecialty = (i: number) => {
    if (!profile) return;
    setProfile({ ...profile, specialties: (profile.specialties ?? []).filter((_, idx) => idx !== i) });
  };

  const uploadMedia = async (file: File, kind: "photo" | "flair_video") => {
    if (!userId) return;
    try {
      const path = await uploadPortfolioFile(userId, file, kind);
      const { error } = await (supabase.from as any)("portfolio_media").insert({
        bartender_id: userId, kind, storage_path: path, sort_order: media.length,
      });
      if (error) throw error;
      toast.success(kind === "photo" ? "Photo added." : "Video added.");
      await load();
    } catch (e: any) { toast.error(e.message ?? "Upload failed."); }
  };

  const updateMediaCaption = async (id: string, caption: string) => {
    await (supabase.from as any)("portfolio_media").update({ caption }).eq("id", id);
  };

  const deleteMedia = async (row: Media) => {
    if (!confirm("Remove this item?")) return;
    await deletePortfolioFile(row.storage_path);
    await (supabase.from as any)("portfolio_media").delete().eq("id", row.id);
    await load();
  };

  const addCert = async () => {
    if (!userId) return;
    const { error } = await (supabase.from as any)("portfolio_certifications").insert({
      bartender_id: userId, name: "New certification", sort_order: certs.length,
    });
    if (error) toast.error(error.message); else await load();
  };
  const saveCert = async (c: Cert) => {
    const { error } = await (supabase.from as any)("portfolio_certifications").update({
      name: c.name, issuer: c.issuer, issued_on: c.issued_on || null, credential_url: c.credential_url,
    }).eq("id", c.id);
    if (error) toast.error(error.message); else toast.success("Saved.");
  };
  const deleteCert = async (id: string) => {
    if (!confirm("Delete certification?")) return;
    await (supabase.from as any)("portfolio_certifications").delete().eq("id", id);
    await load();
  };

  const addVenue = async () => {
    if (!userId) return;
    const { error } = await (supabase.from as any)("portfolio_venues").insert({
      bartender_id: userId, venue_name: "New venue", sort_order: pvenues.length,
    });
    if (error) toast.error(error.message); else await load();
  };
  const saveVenue = async (v: PVenue) => {
    const { error } = await (supabase.from as any)("portfolio_venues").update({
      venue_name: v.venue_name, role: v.role, city: v.city,
      started_on: v.started_on || null, ended_on: v.ended_on || null, description: v.description,
    }).eq("id", v.id);
    if (error) toast.error(error.message); else toast.success("Saved.");
  };
  const uploadVenuePhoto = async (v: PVenue, file: File) => {
    if (!userId) return;
    try {
      if (v.photo_path) await deletePortfolioFile(v.photo_path);
      const path = await uploadPortfolioFile(userId, file, "venue");
      const { error } = await (supabase.from as any)("portfolio_venues").update({ photo_path: path }).eq("id", v.id);
      if (error) throw error;
      await load();
    } catch (e: any) { toast.error(e.message ?? "Upload failed."); }
  };
  const deleteVenue = async (v: PVenue) => {
    if (!confirm("Delete venue?")) return;
    if (v.photo_path) await deletePortfolioFile(v.photo_path);
    await (supabase.from as any)("portfolio_venues").delete().eq("id", v.id);
    await load();
  };

  if (loading) return (
    <div className="min-h-screen bg-background"><ReserveHeader /><main className="mx-auto max-w-5xl px-6 py-10"><p className="text-sm text-muted-foreground">Loading portfolio…</p></main></div>
  );

  return (
    <div className="min-h-screen bg-background">
      <ReserveHeader />
      <main className="mx-auto max-w-5xl px-6 py-10 space-y-12">
        <div className="flex items-end justify-between gap-6 flex-wrap">
          <div>
            <p className="tracking-wide-luxury text-accent">Account</p>
            <h1 className="mt-3 font-display text-3xl">My Portfolio</h1>
            <span className="gold-divider mt-4" />
            <p className="mt-4 text-sm text-muted-foreground max-w-xl">Showcase your craft. Venues see this before booking — add photos, flair videos, certifications and your previous venues.</p>
          </div>
          <div className="flex gap-3">
            {userId && (
              <Link to="/bartenders/$id" params={{ id: userId }} className="border border-border px-4 py-2 text-xs tracking-wide-luxury text-muted-foreground hover:text-foreground hover:border-foreground">Preview public view →</Link>
            )}
            <Link to="/bartender" className="border border-border px-4 py-2 text-xs tracking-wide-luxury text-muted-foreground hover:text-foreground hover:border-foreground">← Back</Link>
          </div>
        </div>

        {/* Profile basics */}
        <section className="border border-border bg-card p-6 space-y-5">
          <h2 className="font-display text-xl">Profile</h2>
          <div className="flex items-start gap-6">
            <div className="flex flex-col items-center gap-2">
              <div className="w-24 h-24 rounded-full bg-muted overflow-hidden border border-border">
                {avatarUrl ? <img src={avatarUrl} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-2xl text-muted-foreground">—</div>}
              </div>
              <label className="text-xs tracking-wide-luxury text-accent cursor-pointer hover:underline">
                Change avatar
                <input type="file" accept="image/*" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) onAvatarUpload(f); }} />
              </label>
            </div>
            <div className="flex-1 space-y-4">
              <div>
                <Label>Headline</Label>
                <Input value={profile?.headline ?? ""} onChange={(e) => setProfile(profile ? { ...profile, headline: e.target.value } : profile)} placeholder="e.g. Award-winning craft cocktail bartender" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Years of experience</Label>
                  <Input type="number" value={profile?.years_experience ?? ""} onChange={(e) => setProfile(profile ? { ...profile, years_experience: e.target.value ? Number(e.target.value) : null } : profile)} />
                </div>
              </div>
              <div>
                <Label>About me</Label>
                <Textarea rows={4} value={profile?.about_me ?? ""} onChange={(e) => setProfile(profile ? { ...profile, about_me: e.target.value } : profile)} />
              </div>
              <div>
                <Label>Specialties</Label>
                <div className="flex gap-2 mt-1">
                  <Input value={specialtyDraft} onChange={(e) => setSpecialtyDraft(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addSpecialty(); } }} placeholder="e.g. Tiki, Classic, Mixology" />
                  <Button type="button" variant="secondary" onClick={addSpecialty}>Add</Button>
                </div>
                <div className="flex flex-wrap gap-2 mt-3">
                  {(profile?.specialties ?? []).map((s, i) => (
                    <button key={i} type="button" onClick={() => removeSpecialty(i)} className="border border-border px-3 py-1 text-xs hover:bg-muted">{s} ✕</button>
                  ))}
                </div>
              </div>
              <Button onClick={saveProfile} disabled={savingProfile}>{savingProfile ? "Saving…" : "Save profile"}</Button>
            </div>
          </div>
        </section>

        {/* Photos */}
        <section className="border border-border bg-card p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-xl">Photos</h2>
            <label className="text-xs tracking-wide-luxury text-accent cursor-pointer hover:underline">
              Upload photo
              <input type="file" accept="image/*" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadMedia(f, "photo"); }} />
            </label>
          </div>
          {media.filter((m) => m.kind === "photo").length === 0 ? (
            <p className="text-sm text-muted-foreground">No photos yet.</p>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {media.filter((m) => m.kind === "photo").map((m) => (
                <div key={m.id} className="border border-border bg-background">
                  {mediaUrls[m.id] && <img src={mediaUrls[m.id]} alt={m.caption ?? ""} className="w-full aspect-square object-cover" />}
                  <div className="p-2 space-y-2">
                    <Input defaultValue={m.caption ?? ""} placeholder="Caption" onBlur={(e) => updateMediaCaption(m.id, e.target.value)} className="text-xs" />
                    <button type="button" onClick={() => deleteMedia(m)} className="text-xs text-destructive hover:underline">Remove</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Flair videos */}
        <section className="border border-border bg-card p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-xl">Flair videos</h2>
            <label className="text-xs tracking-wide-luxury text-accent cursor-pointer hover:underline">
              Upload video
              <input type="file" accept="video/*" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadMedia(f, "flair_video"); }} />
            </label>
          </div>
          {media.filter((m) => m.kind === "flair_video").length === 0 ? (
            <p className="text-sm text-muted-foreground">No videos yet. Show off your flair, technique, or signature pour.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {media.filter((m) => m.kind === "flair_video").map((m) => (
                <div key={m.id} className="border border-border bg-background">
                  {mediaUrls[m.id] && <video src={mediaUrls[m.id]} controls className="w-full aspect-video object-cover bg-black" />}
                  <div className="p-2 space-y-2">
                    <Input defaultValue={m.caption ?? ""} placeholder="Caption" onBlur={(e) => updateMediaCaption(m.id, e.target.value)} className="text-xs" />
                    <button type="button" onClick={() => deleteMedia(m)} className="text-xs text-destructive hover:underline">Remove</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Certifications */}
        <section className="border border-border bg-card p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-xl">Certifications</h2>
            <Button variant="secondary" onClick={addCert}>Add certification</Button>
          </div>
          {certs.length === 0 ? (
            <p className="text-sm text-muted-foreground">e.g. WSET Level 2, BIIAB Personal Licence, Bar Smarts, Coursera Mixology.</p>
          ) : (
            <div className="space-y-3">
              {certs.map((c, i) => (
                <div key={c.id} className="border border-border p-4 grid md:grid-cols-2 gap-3">
                  <div><Label>Name</Label><Input value={c.name} onChange={(e) => setCerts(certs.map((x, j) => j === i ? { ...x, name: e.target.value } : x))} /></div>
                  <div><Label>Issuer</Label><Input value={c.issuer ?? ""} onChange={(e) => setCerts(certs.map((x, j) => j === i ? { ...x, issuer: e.target.value } : x))} /></div>
                  <div><Label>Issued on</Label><Input type="date" value={c.issued_on ?? ""} onChange={(e) => setCerts(certs.map((x, j) => j === i ? { ...x, issued_on: e.target.value } : x))} /></div>
                  <div><Label>Credential URL</Label><Input value={c.credential_url ?? ""} onChange={(e) => setCerts(certs.map((x, j) => j === i ? { ...x, credential_url: e.target.value } : x))} placeholder="https://…" /></div>
                  <div className="md:col-span-2 flex gap-2"><Button size="sm" onClick={() => saveCert(c)}>Save</Button><Button size="sm" variant="ghost" onClick={() => deleteCert(c.id)}>Delete</Button></div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Previous venues */}
        <section className="border border-border bg-card p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-xl">Previous venues</h2>
            <Button variant="secondary" onClick={addVenue}>Add venue</Button>
          </div>
          {pvenues.length === 0 ? (
            <p className="text-sm text-muted-foreground">Bars, hotels, restaurants, pop-ups — list where you've poured.</p>
          ) : (
            <div className="space-y-3">
              {pvenues.map((v, i) => (
                <div key={v.id} className="border border-border p-4 grid md:grid-cols-[120px_1fr] gap-4">
                  <div className="flex flex-col items-center gap-2">
                    <div className="w-28 h-28 bg-muted overflow-hidden border border-border">
                      {venueUrls[v.id] ? <img src={venueUrls[v.id]} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-xs text-muted-foreground">No photo</div>}
                    </div>
                    <label className="text-xs tracking-wide-luxury text-accent cursor-pointer hover:underline">
                      {v.photo_path ? "Replace" : "Add photo"}
                      <input type="file" accept="image/*" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadVenuePhoto(v, f); }} />
                    </label>
                  </div>
                  <div className="grid md:grid-cols-2 gap-3">
                    <div><Label>Venue name</Label><Input value={v.venue_name} onChange={(e) => setPVenues(pvenues.map((x, j) => j === i ? { ...x, venue_name: e.target.value } : x))} /></div>
                    <div><Label>Role</Label><Input value={v.role ?? ""} onChange={(e) => setPVenues(pvenues.map((x, j) => j === i ? { ...x, role: e.target.value } : x))} placeholder="Head bartender" /></div>
                    <div><Label>City</Label><Input value={v.city ?? ""} onChange={(e) => setPVenues(pvenues.map((x, j) => j === i ? { ...x, city: e.target.value } : x))} /></div>
                    <div className="grid grid-cols-2 gap-2">
                      <div><Label>Start</Label><Input type="date" value={v.started_on ?? ""} onChange={(e) => setPVenues(pvenues.map((x, j) => j === i ? { ...x, started_on: e.target.value } : x))} /></div>
                      <div><Label>End</Label><Input type="date" value={v.ended_on ?? ""} onChange={(e) => setPVenues(pvenues.map((x, j) => j === i ? { ...x, ended_on: e.target.value } : x))} /></div>
                    </div>
                    <div className="md:col-span-2"><Label>Description</Label><Textarea rows={2} value={v.description ?? ""} onChange={(e) => setPVenues(pvenues.map((x, j) => j === i ? { ...x, description: e.target.value } : x))} /></div>
                    <div className="md:col-span-2 flex gap-2"><Button size="sm" onClick={() => saveVenue(v)}>Save</Button><Button size="sm" variant="ghost" onClick={() => deleteVenue(v)}>Delete</Button></div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
